import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    Mutation,
    PlayerEcologyWeeklyReport,
    Query,
    QueryGetPlayerEcologyWeeklyReportsArgs,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {PlayerEcologyWeeklyRequestBuilder} from './requestBuilder';
import type {PlayerEcologyWeeklyFilterKeys, PlayerEcologyWeeklyQueryFields} from './types';

@injectable()
export class PlayerEcologyWeeklyService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetPlayerEcologyWeeklyReportsArgs,
        PlayerEcologyWeeklyQueryFields,
        Mutation,
        Subscription,
        BaseHttpCodes,
        PlayerEcologyWeeklyFilterKeys
    >
    implements IDownloadByLinkService<PlayerEcologyWeeklyQueryFields>
{
    private readonly _requestBuilder: PlayerEcologyWeeklyRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const requestBuilder = new PlayerEcologyWeeklyRequestBuilder();
        super(client, requestBuilder, messageLocalizer);
        this._requestBuilder = requestBuilder;
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<PlayerEcologyWeeklyQueryFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<PlayerEcologyWeeklyQueryFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportPlayerEcologyWeeklyReports?.url,
            }))
        );
    }
    get(
        payload: EntityFetchRequestPayload<EntityType, PlayerEcologyWeeklyQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, PlayerEcologyWeeklyReport>> {
        return this.fetchData(payload).pipe(
            map(({responsePayload, ...rest}) => {
                const query = responsePayload.getPlayerEcologyWeeklyReports;
                return {
                    ...rest,
                    requestPayload: payload,
                    responsePayload: {
                        items: query?.items ?? [],
                        total: query?.total_count ?? 0,
                    },
                };
            })
        );
    }
    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
